/**
 * /ref/:code — the copy icon beside the booking reference in the booking
 * confirmation email lands here. Email apps can't run a copy script, so the
 * icon opens this page and one tap copies the code.
 *
 * Deliberately outside Layout and auth: it only echoes the code from its own
 * URL and fetches nothing. Chrome lets the copy happen on load; Safari needs
 * the tap, so the button is always there.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const LOGO = 'https://cdn.resend.app/111fb27a-018d-41a5-ad59-ac11feed64b0';
const CODE_RE = /^[A-Z0-9]{5,8}$/;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older WebViews: fall back to a selected textarea.
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

export default function BookingRefCopy() {
  const { code: raw = '' } = useParams();
  const code = String(raw).trim().toUpperCase();
  const valid = CODE_RE.test(code);
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!valid) return;
    if (await copyText(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [code, valid]);

  useEffect(() => {
    if (!valid) return;
    navigator.clipboard?.writeText(code).then(() => setCopied(true), () => {});
  }, [code, valid]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <img src={LOGO} alt="Defera" className="h-10 w-auto mx-auto mb-10" />
        {valid ? (
          <>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/50">Booking reference</p>
            <p className="mt-3 font-mono text-5xl font-bold tracking-[0.2em] select-all">{code}</p>
            <button
              type="button"
              onClick={copy}
              className="mt-8 w-full rounded-xl bg-white text-black py-3.5 text-sm font-semibold active:scale-[0.99] transition"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <p className="mt-4 text-xs text-white/45">
              This is your Defera booking reference, not your airline confirmation number.
            </p>
          </>
        ) : (
          <p className="text-sm text-white/70">This link doesn&apos;t carry a booking reference.</p>
        )}
        <Link to="/trips" className="mt-8 inline-block text-sm text-white/70 underline underline-offset-4">
          View your trip
        </Link>
      </div>
    </div>
  );
}
